import {
  AnimationData,
  CameraAdaptData,
  HsvValue,
  ModelResId,
  OrbitParams,
  ShaderParams,
} from '@/store/creative/model-editor/index.type'
import _ from 'lodash'
import {
  CookedCategory,
  ModelAnimation,
  ModelMaterial,
} from '@/types/dmaker/category.type'
import {
  BasicModel,
  HsvModel,
  ModelEditorState,
} from '@/store/creative/model-editor/index'

export default {
  setModelCategory(state, cat: CookedCategory) {
    state.category = cat
  },
  hideModelEditor(state) {
    state.visible = false
  },
  showModelEditor(state, { modelPath }) {
    state.visible = true
    state.modelPath = modelPath
    state.loading = true
  },
  setMaterialPath(state, materialPath) {
    state.materialPath = materialPath
  },
  setAnimationPath(state, animationPath) {
    state.animationPath = animationPath
  },
  setModifyConfig(state, config) {
    state.modelModifyConfig = Object.assign({}, state.modelModifyConfig, config)
  },
  setCameraAdaptConfig(state, config) {
    // 第一次写入的时候写入 title/title_en 国际化属性
    if (config && config.orbitParams && config.orbitParams.length) {
      config.orbitParams = config.orbitParams.map((item) => {
        return {
          ...item,
          title: item.title || item.name,
          title_en: item.title_en || item.name,
        }
      })
    }
    state.cameraAdaptConfig = Object.assign({}, state.cameraAdaptConfig, config)
  },
  setModelId(state, modelId: ModelResId) {
    state.modelId = modelId
  },
  setMaterialId(state, materialId: ModelResId) {
    state.materialId = materialId
  },
  setAnimationId(state, animationId: AnimationData['id']) {
    state.animationId = animationId
  },
  setHsvValue(state, hsvValue: HsvValue[] | null) {
    state.hsvValue = hsvValue
  },
  updateLoading(state, loading) {
    state.loading = loading
  },
  setShaderModel(state, data: Partial<ShaderParams>) {
    state.shaderModel = Object.assign({}, state.shaderModel, data)
  },
  setAnimationModel(state, data: Partial<AnimationData>) {
    state.animationModel = data
  },
  setHsvModel(state, data: HsvModel[]) {
    state.hsvModel = _.cloneDeep(data)
  },
  setHsvModelByIndex(state, { data, index }) {
    const temp = _.cloneDeep(state.hsvModel)
    temp[index] = Object.assign({}, temp[index], data)
    state.hsvModel = temp
  },
  setBasicModel(state, data: BasicModel) {
    state.basicModel = Object.assign({}, state.basicModel, data)
  },
  resetBasicModel(state) {
    state.basicModel = {
      translateX: 0,
      translateY: 0,
      translateZ: 0,
      rotateX: 0,
      rotateY: 0,
      rotateZ: 0,
      scaleX: 1,
      scaleY: 1,
      scaleZ: 1,
    }
  },
  resetHsvModel(state) {
    state.hsvModel = [
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: true,
      },
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: true,
      },
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: true,
      },
      {
        hub: -1,
        saturation: -1,
        value: -1,
        disabled: true,
      },
    ]
  },
  resetShaderModel(state) {
    state.shaderModel = {}
  },
  resetAnimationModel(state) {
    state.animationModel = {
      id: '',
      speed: 1,
      duration: 0,
      frameRate: 0,
      events: [],
      rotate: 0,
    }
  },
  resetHsvModelByIndex(state, index: number) {
    const oldData = _.cloneDeep(state.hsvModel)
    let hsvVal = [0, 0, 0]
    if (
      state.resetModel &&
      state.resetModel[JSON.stringify(state.materialId)] &&
      state.resetModel[JSON.stringify(state.materialId)].hsv &&
      state.resetModel[JSON.stringify(state.materialId)].hsv[index]
    ) {
      hsvVal = state.resetModel[JSON.stringify(state.materialId)].hsv[index]
    }
    oldData[index].hub = hsvVal[0]
    oldData[index].saturation = hsvVal[1]
    oldData[index].value = hsvVal[2]
    state.hsvModel = oldData
  },
  // 重置成 h 为 1 的状态
  resetHsvModelToNativeByIndex(state, index: number) {
    const oldData = _.cloneDeep(state.hsvModel)
    const hsvVal = [1, 0, 0]
    oldData[index].hub = hsvVal[0]
    oldData[index].saturation = hsvVal[1]
    oldData[index].value = hsvVal[2]
    state.hsvModel = oldData
  },
  setReplaceData(state, data: ModelEditorState['replaceData']) {
    state.replaceData = data
  },
  setAdaptData(state, data: CameraAdaptData[]) {
    const oldData = _.cloneDeep(state.cameraAdaptConfig.adapt.adaptDatas)
    data.forEach((item: CameraAdaptData) => {
      const targetDataIndex = oldData.findIndex(
        (adaptData: CameraAdaptData) => adaptData.ratio === item.ratio,
      )
      if (targetDataIndex !== -1) {
        oldData[targetDataIndex] = Object.assign(
          {},
          oldData[targetDataIndex],
          item,
        )
      }
      // 为了触发数据联动，需要重新设置适配数据
      state.cameraAdaptConfig.adapt.adaptDatas = oldData
    })
  },
  setOrbitParams(state, data: OrbitParams) {
    const oldData = _.cloneDeep(state.cameraAdaptConfig.orbitParams)
    const targetIndex = oldData.findIndex((item: OrbitParams) => {
      return item.name === data.name
    })
    if (targetIndex !== -1) {
      oldData[targetIndex] = Object.assign({}, oldData[targetIndex], data)
    }
    state.cameraAdaptConfig.orbitParams = oldData
  },
  setReplaceModel(state, data: boolean) {
    state.replaceModel = data
  },
  setModelLoading(state, data: boolean) {
    state.modelLoading = data
  },
  setModelMaterialList(state, data: ModelMaterial[]) {
    state.modelMaterialList = data
  },
  setModelMaterialListByIndex(state, { data, index }) {
    state.modelMaterialList[index] = Object.assign(
      state.modelMaterialList[index],
      data,
    )
  },
  setModelAnimationList(state, data: ModelAnimation[]) {
    state.modelAnimationList = data
  },
  setModelAnimationListByIndex(state, { data, index }) {
    state.modelAnimationList[index] = Object.assign(
      state.modelAnimationList[index],
      data,
    )
  },
  setReplaceMap(state, data: ModelEditorState['replaceMap']) {
    state.replaceMap = data
  },
  setResetModel(state, data: ModelEditorState['resetModel']) {
    state.resetModel = Object.assign({}, state.resetModel, data)
  },
  resetResetModel(state) {
    state.resetModel = {}
  },
  deleteMaterialsConfigById(state, id: string): void {
    if (
      state.modelModifyConfig &&
      state.modelModifyConfig.materials &&
      state.modelModifyConfig.materials.length
    ) {
      const index = state.modelModifyConfig.materials?.findIndex(
        (item) => item.id === id,
      )
      if (index !== -1) {
        state.modelModifyConfig.materials.splice(index, 1)
      }
    }
  },
  deleteAnimationConfigById(state, id: string): void {
    if (
      state.modelModifyConfig &&
      state.modelModifyConfig.animations &&
      state.modelModifyConfig.animations.length
    ) {
      const index = state.modelModifyConfig.animations?.findIndex(
        (item) => item.id === id,
      )
      if (index !== -1) {
        state.modelModifyConfig.animations.splice(index, 1)
      }
    }
  },
}
